/** Pure helpers for the Bookings stay-bar calendar (property lanes × continuous day timeline). */

export type StayCalendarBooking = {
  id: number;
  property_id: number;
  property_nickname?: string | null;
  guest_name?: string | null;
  check_in_date: string;
  check_out_date: string;
  nights?: number | null;
  adults_count?: number | null;
  children_count?: number | null;
  status?: string | null;
  platform?: string | null;
  source?: string | null;
  is_merged_pair?: boolean;
  booking_ref?: string | null;
  reservation_url?: string | null;
  total_payout_amount?: number | null;
  host_payout_amount?: number | null;
  currency?: string | null;
  booked_at?: string | null;
  booked_at_display?: string | null;
  booked_at_fallback?: string | null;
  last_sync?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  external_booking_id?: string | null;
  linked_ical_booking_id?: number | null;
  linked_manual_booking_id?: number | null;
};

export type CalendarBarSourceKind = 'ical' | 'export' | 'manual' | 'merged' | 'other';

export type StayBarInRange = {
  bookingId: number;
  guestLabel: string;
  title: string;
  sourceKind: CalendarBarSourceKind;
  sourceLabel: string;
  booking: StayCalendarBooking;
  checkInYmd: string;
  checkOutYmd: string;
  /** Inclusive day offset from timeline range start. */
  startOffset: number;
  /** Exclusive day offset from timeline range start. */
  endOffsetExclusive: number;
  /** Lane stack index within the property row (0 = top). */
  stack: number;
  clippedStart: boolean;
  clippedEnd: boolean;
};

/** @deprecated Prefer StayBarInRange; kept for month-clip helpers/tests. */
export type StayBarInMonth = {
  bookingId: number;
  guestLabel: string;
  title: string;
  startDay: number;
  endDayExclusive: number;
  stack: number;
  startsBeforeMonth: boolean;
  endsAfterMonth: boolean;
};

export type PropertyStayLane = {
  propertyKey: string;
  propertyId: number;
  nickname: string;
  bars: StayBarInRange[];
  stackCount: number;
};

export type MonthSegment = {
  key: string;
  label: string;
  shortLabel: string;
  startOffset: number;
  dayCount: number;
};

export type TimelineWindow = {
  rangeStart: Date;
  dayCount: number;
};

export function parseYmdLocal(value?: string | null): Date | null {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function ymdKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

export function addMonths(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

export function addDays(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta);
}

export function dayCountBetween(start: Date, endExclusive: Date): number {
  const ms = endExclusive.getTime() - start.getTime();
  return Math.max(0, Math.round(ms / 86400000));
}

export function propertyLaneKey(booking: StayCalendarBooking): string {
  const nick = String(booking.property_nickname || '').trim();
  if (nick) return nick;
  return `Property ${booking.property_id}`;
}

export type TodayStayKind = 'check_in' | 'check_out' | 'ongoing';

export type TodayStayItem = {
  bookingId: number;
  kind: TodayStayKind;
  guestLabel: string;
  propertyNickname: string;
  propertyId: number;
  checkInYmd: string;
  checkOutYmd: string;
  sourceKind: CalendarBarSourceKind;
  sameDay: boolean;
  booking: StayCalendarBooking;
};

export type TodayStaySummary = {
  dayYmd: string;
  checkIns: TodayStayItem[];
  checkOuts: TodayStayItem[];
  ongoing: TodayStayItem[];
  /** Properties with both a check-out and a check-in on this day. */
  turnoverPropertyKeys: string[];
};

function bookingDayYmd(value?: string | null): string {
  const d = parseYmdLocal(value);
  return d ? ymdKey(d) : '';
}

/** Classify a stay relative to a calendar day (Airbnb-style [checkIn, checkOut)). */
export function classifyStayForDay(
  booking: StayCalendarBooking,
  dayYmd: string
): TodayStayKind | null {
  const ci = bookingDayYmd(booking.check_in_date);
  const co = bookingDayYmd(booking.check_out_date);
  if (!ci || !co || !dayYmd) return null;
  if (ci === dayYmd) return 'check_in';
  if (co === dayYmd) return 'check_out';
  if (ci < dayYmd && dayYmd < co) return 'ongoing';
  return null;
}

export function summarizeStaysForDay(
  bookings: StayCalendarBooking[],
  dayYmd: string
): TodayStaySummary {
  const checkIns: TodayStayItem[] = [];
  const checkOuts: TodayStayItem[] = [];
  const ongoing: TodayStayItem[] = [];

  for (let i = 0; i < bookings.length; i += 1) {
    const b = bookings[i];
    const kind = classifyStayForDay(b, dayYmd);
    if (!kind) continue;
    const ci = bookingDayYmd(b.check_in_date);
    const co = bookingDayYmd(b.check_out_date);
    const item: TodayStayItem = {
      bookingId: b.id,
      kind,
      guestLabel: String(b.guest_name || '').trim() || 'Stay',
      propertyNickname: propertyLaneKey(b),
      propertyId: b.property_id,
      checkInYmd: ci,
      checkOutYmd: co,
      sourceKind: calendarBarSourceKind(b),
      sameDay: ci === co,
      booking: b,
    };
    if (kind === 'check_in') checkIns.push(item);
    else if (kind === 'check_out') checkOuts.push(item);
    else ongoing.push(item);
  }

  const byGuestProp = (a: TodayStayItem, b: TodayStayItem) =>
    a.propertyNickname.localeCompare(b.propertyNickname, undefined, { sensitivity: 'base' }) ||
    a.guestLabel.localeCompare(b.guestLabel, undefined, { sensitivity: 'base' });

  checkIns.sort(byGuestProp);
  checkOuts.sort(byGuestProp);
  ongoing.sort(byGuestProp);

  const outProps = new Set(checkOuts.map((x) => x.propertyNickname));
  const turnoverPropertyKeys = Array.from(
    new Set(checkIns.filter((x) => outProps.has(x.propertyNickname)).map((x) => x.propertyNickname))
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  return { dayYmd, checkIns, checkOuts, ongoing, turnoverPropertyKeys };
}

export function calendarBarSourceKind(booking: {
  source?: string | null;
  is_merged_pair?: boolean;
}): CalendarBarSourceKind {
  if (booking.is_merged_pair || booking.source === 'Merged') return 'merged';
  const s = String(booking.source || '').trim();
  if (s === 'iCal') return 'ical';
  if (s === 'AirBnB Export' || s === 'VRBO Export') return 'export';
  if (s === 'Manual') return 'manual';
  return 'other';
}

export function calendarBarSourceLabel(kind: CalendarBarSourceKind): string {
  switch (kind) {
    case 'ical':
      return 'iCal';
    case 'export':
      return 'AirBnB Export';
    case 'manual':
      return 'Manual';
    case 'merged':
      return 'Merged';
    default:
      return 'Other';
  }
}

export function normalizeStayOut(checkIn: Date, checkOut: Date): Date {
  return checkOut <= checkIn
    ? new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate() + 1)
    : checkOut;
}

/** Merge overlapping [start, end) day offsets. */
export function mergeDayIntervals(
  intervals: Array<{ startOffset: number; endOffsetExclusive: number }>
): Array<{ startOffset: number; endOffsetExclusive: number }> {
  const sorted = [...intervals]
    .filter((i) => i.endOffsetExclusive > i.startOffset)
    .sort((a, b) => a.startOffset - b.startOffset || a.endOffsetExclusive - b.endOffsetExclusive);
  if (!sorted.length) return [];
  const out: Array<{ startOffset: number; endOffsetExclusive: number }> = [
    { ...sorted[0] },
  ];
  for (let i = 1; i < sorted.length; i += 1) {
    const cur = sorted[i];
    const last = out[out.length - 1];
    if (cur.startOffset <= last.endOffsetExclusive) {
      last.endOffsetExclusive = Math.max(last.endOffsetExclusive, cur.endOffsetExclusive);
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

/** Vacant [start, end) spans within [0, dayCount) not covered by occupied intervals. */
export function vacancyGapsInRange(
  dayCount: number,
  occupied: Array<{ startOffset: number; endOffsetExclusive: number }>
): Array<{ startOffset: number; endOffsetExclusive: number }> {
  if (dayCount <= 0) return [];
  const merged = mergeDayIntervals(
    occupied.map((o) => ({
      startOffset: Math.max(0, o.startOffset),
      endOffsetExclusive: Math.min(dayCount, o.endOffsetExclusive),
    }))
  );
  const gaps: Array<{ startOffset: number; endOffsetExclusive: number }> = [];
  let cursor = 0;
  for (let i = 0; i < merged.length; i += 1) {
    const block = merged[i];
    if (block.startOffset > cursor) {
      gaps.push({ startOffset: cursor, endOffsetExclusive: block.startOffset });
    }
    cursor = Math.max(cursor, block.endOffsetExclusive);
  }
  if (cursor < dayCount) {
    gaps.push({ startOffset: cursor, endOffsetExclusive: dayCount });
  }
  return gaps;
}

/**
 * Clip an Airbnb-style stay [checkIn, checkOut) into a month window.
 * Returns null when the stay does not overlap the month.
 */
export function clipStayToMonth(
  checkIn: Date,
  checkOut: Date,
  month: Date
): { startDay: number; endDayExclusive: number; startsBeforeMonth: boolean; endsAfterMonth: boolean } | null {
  const monthStart = startOfMonth(month);
  const dim = daysInMonth(month);
  const monthEndExclusive = new Date(month.getFullYear(), month.getMonth(), dim + 1);

  if (!(checkIn < monthEndExclusive && checkOut > monthStart)) {
    return null;
  }

  const startsBeforeMonth = checkIn < monthStart;
  const endsAfterMonth = checkOut > monthEndExclusive;

  const clippedStart = startsBeforeMonth ? monthStart : checkIn;
  const clippedEnd = endsAfterMonth ? monthEndExclusive : checkOut;

  const startDay = clippedStart.getDate();
  let endDayExclusive = clippedEnd.getDate();
  if (clippedEnd.getMonth() !== month.getMonth() || clippedEnd.getFullYear() !== month.getFullYear()) {
    endDayExclusive = dim + 1;
  }
  if (endDayExclusive <= startDay) {
    endDayExclusive = Math.min(dim + 1, startDay + 1);
  }
  return { startDay, endDayExclusive, startsBeforeMonth, endsAfterMonth };
}

export function clipStayToRange(
  checkIn: Date,
  checkOut: Date,
  rangeStart: Date,
  dayCount: number
): { startOffset: number; endOffsetExclusive: number; clippedStart: boolean; clippedEnd: boolean } | null {
  const rangeEndExclusive = addDays(rangeStart, dayCount);
  if (!(checkIn < rangeEndExclusive && checkOut > rangeStart)) {
    return null;
  }
  const clippedStart = checkIn < rangeStart;
  const clippedEnd = checkOut > rangeEndExclusive;
  let startOffset = dayCountBetween(rangeStart, clippedStart ? rangeStart : checkIn);
  let endOffsetExclusive = dayCountBetween(rangeStart, clippedEnd ? rangeEndExclusive : checkOut);
  if (endOffsetExclusive <= startOffset) {
    endOffsetExclusive = Math.min(dayCount, startOffset + 1);
  }
  startOffset = Math.max(0, Math.min(dayCount - 1, startOffset));
  endOffsetExclusive = Math.max(startOffset + 1, Math.min(dayCount, endOffsetExclusive));
  return { startOffset, endOffsetExclusive, clippedStart, clippedEnd };
}

/** Greedy pack of bars that overlap on the day axis into non-overlapping stacks. */
export function packStayStacksByOffset(
  bars: Array<Omit<StayBarInRange, 'stack'>>
): StayBarInRange[] {
  const sorted = [...bars].sort((a, b) => {
    if (a.startOffset !== b.startOffset) return a.startOffset - b.startOffset;
    return a.endOffsetExclusive - b.endOffsetExclusive;
  });
  const stackEnds: number[] = [];
  return sorted.map((bar) => {
    let stack = 0;
    while (stack < stackEnds.length && stackEnds[stack] > bar.startOffset) {
      stack += 1;
    }
    if (stack === stackEnds.length) {
      stackEnds.push(bar.endOffsetExclusive);
    } else {
      stackEnds[stack] = bar.endOffsetExclusive;
    }
    return { ...bar, stack };
  });
}

/** @deprecated Prefer packStayStacksByOffset */
export function packStayStacks(
  bars: Array<Omit<StayBarInMonth, 'stack'>>
): StayBarInMonth[] {
  const sorted = [...bars].sort((a, b) => {
    if (a.startDay !== b.startDay) return a.startDay - b.startDay;
    return a.endDayExclusive - b.endDayExclusive;
  });
  const stackEnds: number[] = [];
  return sorted.map((bar) => {
    let stack = 0;
    while (stack < stackEnds.length && stackEnds[stack] > bar.startDay) {
      stack += 1;
    }
    if (stack === stackEnds.length) {
      stackEnds.push(bar.endDayExclusive);
    } else {
      stackEnds[stack] = bar.endDayExclusive;
    }
    return { ...bar, stack };
  });
}

/**
 * Timeline covers filtered stays (month-aligned) with padding around today.
 * Caps length so the DOM stays manageable.
 */
export function resolveTimelineWindow(
  bookings: StayCalendarBooking[],
  now = new Date(),
  opts?: { monthsBefore?: number; monthsAfter?: number; maxMonths?: number }
): TimelineWindow {
  const monthsBefore = opts?.monthsBefore ?? 3;
  const monthsAfter = opts?.monthsAfter ?? 12;
  const maxMonths = opts?.maxMonths ?? 36;

  let start = addMonths(startOfMonth(now), -monthsBefore);
  let endExclusive = addMonths(startOfMonth(now), monthsAfter + 1);

  for (let i = 0; i < bookings.length; i += 1) {
    const b = bookings[i];
    const ci = parseYmdLocal(b.check_in_date);
    const co = parseYmdLocal(b.check_out_date);
    if (!ci || !co) continue;
    const out = normalizeStayOut(ci, co);
    if (startOfMonth(ci) < start) start = startOfMonth(ci);
    const lastNight = addDays(out, -1);
    const afterLast = addMonths(startOfMonth(lastNight), 1);
    if (afterLast > endExclusive) endExclusive = afterLast;
  }

  let dayCount = dayCountBetween(start, endExclusive);
  const maxDays = maxMonths * 31;
  if (dayCount > maxDays) {
    // Prefer keeping "now" near the left third of a capped window
    const preferredStart = addMonths(startOfMonth(now), -Math.floor(maxMonths / 3));
    start = preferredStart;
    endExclusive = addMonths(preferredStart, maxMonths);
    dayCount = dayCountBetween(start, endExclusive);
  }
  if (dayCount < 28) {
    start = startOfMonth(now);
    endExclusive = addMonths(start, 2);
    dayCount = dayCountBetween(start, endExclusive);
  }
  return { rangeStart: start, dayCount };
}

export function buildMonthSegments(rangeStart: Date, dayCount: number): MonthSegment[] {
  const segments: MonthSegment[] = [];
  let offset = 0;
  while (offset < dayCount) {
    const day = addDays(rangeStart, offset);
    const monthStart = startOfMonth(day);
    const dim = daysInMonth(day);
    const dayOfMonth = day.getDate();
    const remainingInMonth = dim - dayOfMonth + 1;
    const span = Math.min(remainingInMonth, dayCount - offset);
    segments.push({
      key: ymdKey(monthStart),
      label: monthLabel(monthStart),
      shortLabel: monthStart.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
      startOffset: offset,
      dayCount: span,
    });
    offset += span;
  }
  return segments;
}

export function buildPropertyStayLanesForRange(
  bookings: StayCalendarBooking[],
  rangeStart: Date,
  dayCount: number
): PropertyStayLane[] {
  const byProp = new Map<
    string,
    { propertyId: number; nickname: string; raw: Array<Omit<StayBarInRange, 'stack'>> }
  >();

  for (let i = 0; i < bookings.length; i += 1) {
    const b = bookings[i];
    const checkIn = parseYmdLocal(b.check_in_date);
    const checkOut = parseYmdLocal(b.check_out_date);
    if (!checkIn || !checkOut) continue;
    const out = normalizeStayOut(checkIn, checkOut);
    const clipped = clipStayToRange(checkIn, out, rangeStart, dayCount);
    if (!clipped) continue;

    const nickname = propertyLaneKey(b);
    const guestLabel = String(b.guest_name || '').trim() || 'Stay';
    const sourceKind = calendarBarSourceKind(b);
    const sourceLabel = calendarBarSourceLabel(sourceKind);
    const title = [
      guestLabel,
      sourceLabel,
      `${ymdKey(checkIn)} → ${ymdKey(out)}`,
      b.status || '',
      b.platform || '',
    ]
      .filter(Boolean)
      .join(' · ');

    let bucket = byProp.get(nickname);
    if (!bucket) {
      bucket = { propertyId: b.property_id, nickname, raw: [] };
      byProp.set(nickname, bucket);
    }
    bucket.raw.push({
      bookingId: b.id,
      guestLabel,
      title,
      sourceKind,
      sourceLabel,
      booking: b,
      checkInYmd: ymdKey(checkIn),
      checkOutYmd: ymdKey(out),
      startOffset: clipped.startOffset,
      endOffsetExclusive: clipped.endOffsetExclusive,
      clippedStart: clipped.clippedStart,
      clippedEnd: clipped.clippedEnd,
    });
  }

  const lanes: PropertyStayLane[] = Array.from(byProp.values()).map((bucket) => {
    const bars = packStayStacksByOffset(bucket.raw);
    const stackCount = bars.reduce((m, bar) => Math.max(m, bar.stack + 1), 1);
    return {
      propertyKey: bucket.nickname,
      propertyId: bucket.propertyId,
      nickname: bucket.nickname,
      bars,
      stackCount,
    };
  });

  lanes.sort((a, b) => a.nickname.localeCompare(b.nickname, undefined, { sensitivity: 'base' }));
  return lanes;
}

/** Single-month lanes (tests / narrow callers). */
export function buildPropertyStayLanes(
  bookings: StayCalendarBooking[],
  month: Date
): PropertyStayLane[] {
  const rangeStart = startOfMonth(month);
  const dayCount = daysInMonth(month);
  return buildPropertyStayLanesForRange(bookings, rangeStart, dayCount);
}

/** Prefer the current month when any filtered stay overlaps it; else earliest check-in month. */
export function defaultCalendarMonth(bookings: StayCalendarBooking[], now = new Date()): Date {
  const current = startOfMonth(now);
  const dim = daysInMonth(current);
  const currentEnd = new Date(current.getFullYear(), current.getMonth(), dim + 1);
  for (let i = 0; i < bookings.length; i += 1) {
    const b = bookings[i];
    const ci = parseYmdLocal(b.check_in_date);
    const co = parseYmdLocal(b.check_out_date);
    if (!ci || !co) continue;
    const out = normalizeStayOut(ci, co);
    if (ci < currentEnd && out > current) {
      return current;
    }
  }
  let earliest: Date | null = null;
  for (let i = 0; i < bookings.length; i += 1) {
    const b = bookings[i];
    const ci = parseYmdLocal(b.check_in_date);
    if (!ci) continue;
    if (!earliest || ci < earliest) earliest = ci;
  }
  return earliest ? startOfMonth(earliest) : current;
}

export function monthLabel(month: Date): string {
  return month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function offsetForDate(rangeStart: Date, day: Date): number {
  return dayCountBetween(rangeStart, day);
}
